// Tractor Supply — Riley's crew. What the page suggests a caller says, per customer.
// Deliberately NOT the test doc's scenario scripts: the page shows customers and lets the
// conversation go where it goes; these are only starting lines and the answers a caller
// needs when Riley asks (her reason, which order). Keyed by TscCustomer.id.

import { RILEY, TscPersona } from "../tsc/tscDemos";

export interface TscSuggestions {
  /** "Try asking" before the call: the opener first. */
  tryLines: string[];
  /** The caller's answer when Riley asks why they want to cancel. */
  reason?: string;
  /** The caller's answer when Riley asks which of several orders. */
  whichOrder?: string;
}

const CANCEL = "Hi, I'd like to cancel an order.";

export const TSC_SUGGESTIONS: Record<string, TscSuggestions> = {
  "273023690": { tryLines: [CANCEL, "I found it cheaper somewhere else.", "Hi, I forgot my password."], reason: "I found it cheaper somewhere else." },
  "516816845": { tryLines: [CANCEL, "The cat food.", "I changed my mind.", "I still need it cancelled."], reason: "I changed my mind.", whichOrder: "The cat food." },
  "785628916": { tryLines: [CANCEL, "I don't need it anymore.", "Let me speak to a supervisor."], reason: "I don't need it anymore." },
  "433915192": { tryLines: ["Hi, I want to return a dog collar I bought.", "What time does the Franklin store close?", "Where is my package?"] },
  "325952057": { tryLines: [CANCEL, "I don't need it anymore.", "I still need it cancelled."], reason: "I don't need it anymore.", whichOrder: "The wet dog food." },
  "399569218": { tryLines: [CANCEL, "It won't get here in time.", "I still need it cancelled."], reason: "It won't get here in time.", whichOrder: "The dog camera." },
  "401357266": { tryLines: [CANCEL, "It won't get here in time.", "I still need it cancelled."], reason: "It won't get here in time.", whichOrder: "The chicken coop." },
  "794672783": { tryLines: [CANCEL, "I changed my mind.", "I still need it cancelled.", "A phone call, please."], reason: "I changed my mind." },
  "802627905": { tryLines: [CANCEL, "I don't need it anymore."], reason: "I don't need it anymore." },
  "858054517": { tryLines: [CANCEL, "I don't need it anymore."], reason: "I don't need it anymore." },
  "660736385": { tryLines: [CANCEL, "It won't get here in time."], reason: "It won't get here in time." },
  "834397787": { tryLines: [CANCEL, "It won't get here in time."], reason: "It won't get here in time." },
  "890583835": { tryLines: [CANCEL, "I found it cheaper somewhere else."], reason: "I found it cheaper somewhere else." },
};

// On every call, for a crew that asks for the email first (the multilingual demo).
// The copy names the agent (Riley, or Mike on the male-voice twin) with the right pronouns.
export function universalRules(a: TscPersona = RILEY): string[] {
  return [
    "Say the email the way you normally would, for example \"emma dot ward at gmail dot com\".",
    `${a.name} reads your email and your zip back to you. Say "yes", or correct ${a.them}.`,
    `${a.name}'s wording changes a little from call to call. Check what ${a.they} does, not ${a.their} exact words.`,
    "Every call should end with: \"Thank you for contacting Tractor Supply Company. Hope you have a great day.\"",
  ];
}

// On every call, for a crew that asks for the customer ID first (the main TSC demo).
export function universalRulesCustomerId(a: TscPersona = RILEY): string[] {
  return [
    `When ${a.name} asks for your customer ID, read it from your customer card, for example "two seven three, zero two three, six nine zero".`,
    `${a.name} reads your customer ID and your zip back to you. Say "yes", or correct ${a.them}.`,
    `Don't have your customer ID? Say so, and ${a.name} uses your email and zip instead.`,
    `For a password reset ${a.name} asks for your email, and spells it back to you letter by letter.`,
    `${a.name}'s wording changes a little from call to call. Check what ${a.they} does, not ${a.their} exact words.`,
    "Every call should end with: \"Thank you for contacting Tractor Supply Company. Hope you have a great day.\"",
  ];
}

export const UNIVERSAL_RULES = universalRules(RILEY);
export const UNIVERSAL_RULES_CUSTOMER_ID = universalRulesCustomerId(RILEY);

export function tscRules(verifyWith: "customerId" | "email", a: TscPersona = RILEY): string[] {
  return verifyWith === "customerId" ? universalRulesCustomerId(a) : universalRules(a);
}

const EMAILS_TO_NURIX = "The payment link, order confirmation and password reset email go to a Nurix test inbox.";
const EMAILS_TO_YOU =
  "The payment link, order confirmation and password reset email go to the Nurix team's test inboxes. To get them yourself, add your email on the call page.";

/** "Before your session" on the landing; with the call page's email box (TscDemo.testerEmail) the email line says how to use it. */
export function beforeSession(testerEmail: boolean, a: TscPersona = RILEY): string[] {
  return [
    "Click Reset demo data at the top of the page before you test. Each order can only be cancelled once per reset.",
    testerEmail ? EMAILS_TO_YOU : EMAILS_TO_NURIX,
    "Store calls, callbacks and teammate transfers ring phone numbers the Nurix team sets up. On the call page, add a warm transfer number to choose who gets the transfer.",
    `Requests ${a.name} sends to a warehouse or vendor are recorded in the demo system only. Nobody is contacted, and the follow-up emails or calls ${a.they} mentions aren't sent.`,
  ];
}

export const BEFORE_SESSION = beforeSession(false, RILEY);
